import { v4 as uuid } from 'uuid';
import { TranslatableField } from '../TranslatableField';
import { ContentBlockType } from './ContentBlockType';

export type ContentBlockDataModel = {};
export type ContentBlockSettingsModel = {};

export interface ContentBlockModel<
  TData extends ContentBlockDataModel = ContentBlockDataModel,
  TSettings extends ContentBlockSettingsModel = ContentBlockSettingsModel,
> {
  id: string;
  type: ContentBlockType;
  order: number;
  newData: TData;
  settings: TSettings;
}

export interface ContentBlockDataWithButtonModel {
  button?: ContentBlockButtonData;
}

export interface ContentBlockSettingsWithButtonModel {
  withButton: boolean;
  button?: ContentBlockButtonSettings;
}

/* LIST */

export enum ContentBlockListItemType {
  Speaker = 'speaker',
  Accordion = 'accordion',
  MultiRows = 'multirows',
  MultiColumns = 'multicolumns',
}

export enum ContentBlockListItemOrientation {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

export interface ContentBlockListItemModel {
  id: string;
}

/* DATA & SETTINGS */

export interface ContentBlockButtonData {
  url: TranslatableField;
  label: TranslatableField;
}

export interface ContentBlockButtonSettings {
  style: ContentBlockButtonStyle;
}

export interface ContentBlockImageSettings {
  size: ContentBlockImageSize;
  position: ContentBlockImagePosition;
}

export interface ContentBlockImageData {
  id: string;
  fileUploadId: TranslatableField<string | null>; // this is the same as file upload `resourceId`
  altText: TranslatableField;
  url: TranslatableField;
  originalUrl: TranslatableField;
  coordinates: {
    left: TranslatableField<number | null>;
    top: TranslatableField<number | null>;
    width: TranslatableField<number | null>;
    height: TranslatableField<number | null>;
  };
  link?: TranslatableField; // link to a page or an external URL
}

export type ContentBlockFileData = {
  id: string;
  fileUploadId: TranslatableField<string | null>; // this is the same as file upload `resourceId`
  originalFileName: TranslatableField;
  type: TranslatableField;
};

/* ENUMS */

export enum ContentBlockButtonStyle {
  Primary = 'primary',
  Secondary = 'secondary',
  Link = 'link',
}

export const contentBlockButtonStyleMap: Record<ContentBlockButtonStyle, string> = {
  [ContentBlockButtonStyle.Primary]: 'primary',
  [ContentBlockButtonStyle.Secondary]: 'secondary-gray',
  [ContentBlockButtonStyle.Link]: 'link-color',
};

export enum ContentBlockTextHorizontalAlignment {
  Center = 'center',
  Left = 'left',
}

export const contentBlockTextHorizontalAlignmentModifiersMap: Record<ContentBlockTextHorizontalAlignment, string> = {
  [ContentBlockTextHorizontalAlignment.Left]: '-align-left',
  [ContentBlockTextHorizontalAlignment.Center]: '-align-center',
};

export enum ContentBlockTextVerticalAlignment {
  Center = 'center',
  Top = 'top',
}

export const contentBlockTextVerticalAlignmentModifiersMap: Record<ContentBlockTextVerticalAlignment, string> = {
  [ContentBlockTextVerticalAlignment.Center]: '-align-center',
  [ContentBlockTextVerticalAlignment.Top]: '-align-top',
};

export enum ContentBlockImageSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export const contentBlockImageSizeModifiersMap: Record<ContentBlockImageSize, string> = {
  [ContentBlockImageSize.Small]: '-small',
  [ContentBlockImageSize.Medium]: '-medium',
  [ContentBlockImageSize.Large]: '-large',
};

export enum ContentBlockImagePosition {
  Left = 'left',
  Top = 'top',
  Right = 'right',
  Bottom = 'bottom',
}

export const contentBlockImagePositionModifiersMap: Record<ContentBlockImagePosition, string> = {
  [ContentBlockImagePosition.Left]: '-image-left',
  [ContentBlockImagePosition.Right]: '-image-right',
  [ContentBlockImagePosition.Top]: '-image-top',
  [ContentBlockImagePosition.Bottom]: '-image-bottom',
};

export enum ContentBlockImagePlacement {
  Alternate = 'alternate',
  Left = 'left',
  Right = 'right',
}

export const contentBlockImagePlacementModifiersMap: Record<ContentBlockImagePlacement, string> = {
  [ContentBlockImagePlacement.Alternate]: '-image-alternate',
  [ContentBlockImagePlacement.Left]: '-image-left',
  [ContentBlockImagePlacement.Right]: '-image-right',
};

export enum ContentBlockVideoPosition {
  Left = 'left',
  Top = 'top',
  Right = 'right',
  Bottom = 'bottom',
}

export const contentBlockVideoPositionModifiersMap: Record<ContentBlockVideoPosition, string> = {
  [ContentBlockVideoPosition.Left]: '-video-left',
  [ContentBlockVideoPosition.Right]: '-video-right',
  [ContentBlockVideoPosition.Top]: '-video-top',
  [ContentBlockVideoPosition.Bottom]: '-video-bottom',
};

export function createContentBlockImageData(): ContentBlockImageData {
  return {
    id: uuid(),
    fileUploadId: { fr: null, en: null },
    altText: { fr: '', en: '' },
    url: { fr: '', en: '' },
    originalUrl: { fr: '', en: '' },
    coordinates: {
      left: { fr: null, en: null },
      top: { fr: null, en: null },
      width: { fr: null, en: null },
      height: { fr: null, en: null },
    },
  };
}

export function createContentBlockImageDataWithLink(): ContentBlockImageData {
  return {
    ...createContentBlockImageData(),
    link: { fr: '', en: '' },
  };
}

export function createContentBlockFileData(number?: number): ContentBlockFileData {
  return {
    id: uuid(),
    fileUploadId: { fr: null, en: null },
    originalFileName: {
      fr: `Fichier${number ? ` ${number.toString()}` : ''}`,
      en: `File${number ? ` ${number.toString()}` : ''}`,
    },
    type: { fr: '', en: '' },
  };
}
